// NOTE: MIME type the server stores an assistant-written HTML page under
// (NSAI-3933). Every client decides "is this a previewable page?" from it.
export const HTML_PREVIEW_MIME_TYPE = "text/html" as const;

// NOTE: Restrictive Content-Security-Policy injected into every HTML preview.
// The preview frame is already an opaque origin (web: iframe sandbox without
// allow-same-origin; mobile: WebView loaded from inline source), but that alone
// does NOT block outbound network: a malicious or prompt-injected page could
// still fetch()/sendBeacon() the rendered (de-anonymized) content out, or load a
// remote tracking pixel/font. This CSP denies all network egress, external
// resource loads, form posts, base-tag hijacking, and nested frames while still
// letting the page run its OWN inline scripts/styles, so a self-contained
// generated page renders unchanged but cannot exfiltrate (NSAI-3933).
export const HTML_PREVIEW_CONTENT_SECURITY_POLICY = [
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  "img-src data:",
  "font-src data:",
  "media-src data:",
  "form-action 'none'",
  "base-uri 'none'",
  "frame-src 'none'",
].join("; ");

// NOTE: NSAI-5415 — the bytes the mobile WebView document always starts with,
// whatever the page contains. A meta CSP binds only when the parser files it
// in <head>, and `prepareHtmlPreviewDocument` decides where its meta goes by
// matching the page's own markup: a page that opens with a no-break space
// before its doctype passes the match, then the parser reads that space as
// text, opens <body> and ignores the spliced meta, leaving the page's scripts
// free to fetch and beacon its content out. Starting from bytes we write
// instead takes the markup out of it: the doctype and the meta are complete
// tokens before the first byte of the page is read, so the meta always lands
// first in the implicit <head> and binds on insertion, and nothing the page
// writes later can move or disable it. The page's own doctype is then ignored
// (the document stays in standards mode) and its <html> attributes are merged
// onto the root, so `lang` and `dir` survive.
export const HTML_PREVIEW_WEBVIEW_PREAMBLE =
  `<!doctype html><meta http-equiv="Content-Security-Policy" content="${HTML_PREVIEW_CONTENT_SECURITY_POLICY}">` as const;

// NOTE: What `MessageEvent.origin` reads for a sandboxed frame without
// `allow-same-origin` — the serialisation of an opaque origin. The embedder
// requires it (together with the sender being its own frame window) before
// accepting a bridge port.
export const HTML_PREVIEW_OPAQUE_ORIGIN = "null" as const;

// NOTE: `allow-scripts` lets the page run its own JS. `allow-modals` is what
// makes a generated page's own `window.print()`, `alert`, `confirm` and
// `prompt` work at all (the sandbox otherwise ignores them) — an annoyance at
// worst, not an escape: `allow-same-origin` stays absent, so the frame cannot
// reach the app's DOM, cookies, storage or service workers. Kept as one string
// so the web iframe and the Playwright assertion never drift.
export const HTML_PREVIEW_IFRAME_SANDBOX =
  "allow-scripts allow-modals" as const;

// NOTE: NSAI-5825 — the frame the web app actually loads. A `srcdoc` document
// is a LOCAL-SCHEME document, so it inherits its embedder's Content-Security-
// Policy, and CSP policies only ever intersect: the app is served
// `script-src 'self'` (ops/caddy, ops/nginx), so an inherited policy killed
// every inline script in a generated page while `style-src 'unsafe-inline'`
// let the styling through — a page that looked finished and did nothing. The
// page's own meta CSP cannot lift an inherited ban, and no sandbox flag can
// either. A document fetched over http(s) inherits nothing, so the frame now
// loads this shell from a real URL and the page is written INTO it; the
// shell's own response carries the preview policy (see the route exceptions in
// ops/caddy/* and ops/nginx/*). Kept as a root-level path so the SPA's
// history fallback never swallows it.
export const HTML_PREVIEW_FRAME_PATH = "/html-preview-frame.html" as const;

// NOTE: NSAI-5825 — the policy the SHELL's own response must carry, and the
// single source of truth the reverse proxies are checked against (see the
// preview-frame route in ops/caddy/* and ops/nginx/*, and the drift test in
// web/tests/common/html-preview.spec.ts). It is the meta policy plus
// `frame-ancestors 'self'`: a meta tag cannot express frame-ancestors, and the
// shell must not carry `X-Frame-Options: DENY` the way the rest of the app
// does — DENY refuses to render a document in ANY frame, same-origin included,
// which would stop the preview loading at all. Keeping the two in one place is
// what stops a proxy edit from silently reinstating the app's `script-src
// 'self'` and killing every generated page's JavaScript again.
export const HTML_PREVIEW_FRAME_CONTENT_SECURITY_POLICY =
  `${HTML_PREVIEW_CONTENT_SECURITY_POLICY}; frame-ancestors 'self'` as const;

// NOTE: NSAI-5825 — the shell announces itself to its embedder and takes the
// page in reply. The shell writes exactly ONE document per load: `document.
// open()` tears down the window's message listener with the document it
// replaces (verified in Chromium), so a second post after the first is
// silently ignored and a new page needs a fresh frame. That is a property
// worth keeping rather than working around — it puts a hard ceiling of one
// document on anything that can post into the frame.
export const HTML_PREVIEW_FRAME_READY_MESSAGE_TYPE =
  "nsai-html-preview:frame-ready" as const;
export const HTML_PREVIEW_DOCUMENT_MESSAGE_TYPE =
  "nsai-html-preview:document" as const;

// NOTE: Filename used when a page carries no <title> to name itself after.
export const HTML_PREVIEW_DEFAULT_FILE_NAME = "page.html" as const;

// NOTE: NSAI-5659 (part 2) — what a rendered page posts when it throws: the
// first uncaught error, once per document (an unhandled rejection is not
// reported — it rarely stops a page from rendering). The web
// embedder accepts it only from its own frame window; the mobile WebView
// receives it through window.ReactNativeWebView.
export const HTML_PREVIEW_RENDER_ERROR_MESSAGE_TYPE =
  "nsai-html-preview:render-error" as const;

// NOTE: Client-side cap on the reported text; the server bounds it again.
export const HTML_PREVIEW_RENDER_ERROR_MAX_CHARS = 500;

// NOTE: The socket event a client sends the report on (server: ChatGateway
// handleHtmlPageRenderError).
export const HTML_PAGE_RENDER_ERROR_EVENT = "htmlPageRenderError" as const;

// NOTE: NSAI-5659 — the reporter written into every previewed page ahead of
// the page's own scripts. Pre-ES2015 and dependency-free, like the frame shell:
// it runs inside the page, under the page's CSP ('unsafe-inline' is allowed).
// It only listens; it changes nothing the page does. Only uncaught errors count
// — a rejected promise rarely stops a page from rendering — and the browser's
// benign ResizeObserver loop notice is ignored, so a page that renders fine is
// never sent off to be rewritten.
export const HTML_PREVIEW_RENDER_ERROR_REPORTER_SCRIPT = `<script>(function(){var sent=false;var report=function(text){if(sent){return;}sent=true;var message={type:"${HTML_PREVIEW_RENDER_ERROR_MESSAGE_TYPE}",error:String(text).slice(0,${String(HTML_PREVIEW_RENDER_ERROR_MAX_CHARS)})};try{if(window.ReactNativeWebView){window.ReactNativeWebView.postMessage(JSON.stringify(message));return;}}catch(ignored){}try{window.parent.postMessage(message,"*");}catch(ignored){}};window.addEventListener("error",function(event){var text=(event&&event.message)||"Script error";if(/ResizeObserver loop/.test(text)){return;}report(text);});})();</script>`;
